import { Router } from '@angular/router';

export type InteractionContext = {
    interactionMode: 'zoom' | 'redirection';
    chartDesign: any;
    chartQuery: any;
    clickedData: any;
    router?: Router;
};

export type ZoomContext = {
    zoomTargetAxis: 'X' | 'Y';
    chartDesign: any;
    chartQuery: any;
    clickedData: any;
};

export type RedirectionContext = {
    redirectionTarget: 'timeline' | 'risk_management' | 'maps' | 'forge';
    chartQuery: any;
    clickedData: any;
    router: Router;
};

export type ZoomCase = {
    match: (ctx: ZoomContext) => boolean;
    action: (ctx: ZoomContext) => any;
};

export type RedirectionCase = {
    match: (ctx: RedirectionContext) => boolean;
    action: (ctx: RedirectionContext) => void;
};

export class ChartInteractionHandler {

    ////////////////////////////////////////////////////////////////////////////
    // GESTION DU ZOOM
    ////////////////////////////////////////////////////////////////////////////

    private zoomCases: ZoomCase[] = [
        // ZOOM SUR AXE X

        // Cas 1 : Zoom sur X (date) => descend d'un niveau de binning + filtre sur la période cliquée
        {
            match: ctx => {
                const fieldObj = ctx.chartQuery.field && Object.values(ctx.chartQuery.field)[0];
                return ctx.zoomTargetAxis === 'X' && fieldObj && fieldObj.type === 'date';
            },
            action: ctx => {
                const fieldKey = this.getFirstFieldKey(ctx.chartQuery);
                if (!fieldKey) return ctx.chartQuery;

                const field = ctx.chartQuery.field[fieldKey];
                if (!field?.options) return ctx.chartQuery;

                const nextBinning = this.getNextBinning(field.options.binning);
                const { startDate, endDate } = this.calculateDateRange(
                    ctx.clickedData?.x,
                    field.options.binning
                );

                if (!startDate || !endDate) return ctx.chartQuery;

                return {
                    ...ctx.chartQuery,
                    field: {
                        ...ctx.chartQuery.field,
                        [fieldKey]: {
                            ...field,
                            options: {
                                ...field.options,
                                binning: nextBinning
                            }
                        }
                    },
                    filters: {
                        ...ctx.chartQuery.filters,
                        [fieldKey]: this.createDateFilter(startDate, endDate)
                    }
                };
            }
        },

        // Cas 2 : Zoom sur X (non-date) => transformation X en X (field) + Y (count)
        {
            match: ctx => {
                const fieldObj = ctx.chartQuery.field && Object.values(ctx.chartQuery.field)[0];
                const hasSeries = ctx.chartQuery.series && Object.keys(ctx.chartQuery.series).length > 0;
                return ctx.zoomTargetAxis === 'X' && fieldObj && fieldObj.type !== 'date' && !hasSeries;
            },
            action: ctx => {
                const fieldKey = this.getFirstFieldKey(ctx.chartQuery);
                if (!fieldKey) return ctx.chartQuery;

                const field = ctx.chartQuery.field[fieldKey];
                if (!field) return ctx.chartQuery;

                return this.createFieldCountQuery(ctx.chartQuery, fieldKey, field.type);
            }
        },

        // ZOOM SUR AXE Y

        // Cas 3 : Zoom sur Y (non-date) + X (date) => transformation Y en X/count + filtre temporel sur X
        {
            match: ctx => {
                const aggObj = ctx.chartQuery.aggregation && Object.values(ctx.chartQuery.aggregation)[0];
                const fieldObj = ctx.chartQuery.field && Object.values(ctx.chartQuery.field)[0];
                const hasSeries = ctx.chartQuery.series && Object.keys(ctx.chartQuery.series).length > 0;
                return ctx.zoomTargetAxis === 'Y' && aggObj && aggObj.type !== 'date' &&
                    fieldObj && fieldObj.type === 'date' && !hasSeries;
            },
            action: ctx => {
                const aggKey = this.getFirstAggregationKey(ctx.chartQuery);
                if (!aggKey) return ctx.chartQuery;

                const aggField = ctx.chartQuery.aggregation[aggKey];
                if (!aggField) return ctx.chartQuery;

                const newQuery = this.createFieldCountQuery(ctx.chartQuery, aggKey, aggField.type);

                const dateFieldKey = this.getFirstFieldKey(ctx.chartQuery);
                if (!dateFieldKey) return newQuery;

                const field = ctx.chartQuery.field[dateFieldKey];
                const { startDate, endDate } = this.calculateDateRange(
                    ctx.clickedData?.x,
                    field?.options?.binning
                );

                if (startDate && endDate) {
                    newQuery.filters = {
                        ...newQuery.filters,
                        [dateFieldKey]: this.createDateFilter(startDate, endDate)
                    };
                }

                return newQuery;
            }
        },

        // Cas 4 : Zoom sur Y (non-date) + X (non-date) => transformation Y en X/count (sans filtre)
        {
            match: ctx => {
                const aggObj = ctx.chartQuery.aggregation && Object.values(ctx.chartQuery.aggregation)[0];
                const fieldObj = ctx.chartQuery.field && Object.values(ctx.chartQuery.field)[0];
                const hasSeries = ctx.chartQuery.series && Object.keys(ctx.chartQuery.series).length > 0;
                return ctx.zoomTargetAxis === 'Y' && aggObj && aggObj.type !== 'date' &&
                    (!fieldObj || fieldObj.type !== 'date') && !hasSeries;
            },
            action: ctx => {
                const aggKey = this.getFirstAggregationKey(ctx.chartQuery);
                if (!aggKey) return ctx.chartQuery;

                const aggField = ctx.chartQuery.aggregation[aggKey];
                if (!aggField) return ctx.chartQuery;

                return this.createFieldCountQuery(ctx.chartQuery, aggKey, aggField.type);
            }
        }
    ];

    ////////////////////////////////////////////////////////////////////////////
    // GESTION DE LA REDIRECTION
    ////////////////////////////////////////////////////////////////////////////

    private redirectionCases: RedirectionCase[] = [
        // Timeline
        {
            match: ctx => ctx.redirectionTarget === 'timeline',
            action: ctx => {
                const params: any = {
                    mode: 'timeline'
                };

                const extractedParams = this.extractQueryParams(ctx.chartQuery, ctx.clickedData);
                Object.assign(params, extractedParams);
                ctx.router.navigate(['/project/report'], { queryParams: params });
            }
        },

        // Risk Management
        {
            match: ctx => ctx.redirectionTarget === 'risk_management',
            action: ctx => {
                const params: any = {
                    mode: 'map'
                };

                const extractedParams = this.extractQueryParams(ctx.chartQuery, ctx.clickedData);
                Object.assign(params, extractedParams);

                if (params.endDate) {
                    params.showEndDate = true;
                }

                ctx.router.navigate(['/project/risks'], { queryParams: params });
            }
        },

        // Maps
        {
            match: ctx => ctx.redirectionTarget === 'maps',
            action: ctx => {
                const params: any = {
                    mode: 'realtime'
                };

                const extractedParams = this.extractQueryParams(ctx.chartQuery, ctx.clickedData);
                Object.assign(params, extractedParams);

                if (ctx.clickedData?.lat && ctx.clickedData?.lon) {
                    params.lat = ctx.clickedData.lat;
                    params.lon = ctx.clickedData.lon;
                }

                ctx.router.navigate(['/project/viewer/maps'], { queryParams: params });
            }
        },

        // Forge 3D
        {
            match: ctx => ctx.redirectionTarget === 'forge',
            action: ctx => {
                const params: any = {};

                const extractedParams = this.extractQueryParams(ctx.chartQuery, ctx.clickedData);
                Object.assign(params, extractedParams);

                if (ctx.clickedData?.modelId) {
                    params.modelId = ctx.clickedData.modelId;
                }

                ctx.router.navigate(['/project/viewer/forge'], { queryParams: params });
            }
        }
    ];


    // Fonctions publiques

    /**
     * Gère le zoom en fonction de l'axe ciblé
     */
    handleZoom(
        zoomTargetAxis: 'X' | 'Y',
        chartDesign: any,
        chartQuery: any,
        clickedData: any
    ): any {
        const ctx: ZoomContext = { zoomTargetAxis, chartDesign, chartQuery, clickedData };
        const matchedCase = this.zoomCases.find(c => c.match(ctx));

        if (matchedCase) {
            return matchedCase.action(ctx);
        }

        console.warn('⚠️ Aucun cas de zoom correspondant');
        return null;
    }

    /**
     * Gère la redirection vers une page cible
     */
    handleRedirection(
        redirectionTarget: 'timeline' | 'risk_management' | 'maps' | 'forge',
        chartQuery: any,
        clickedData: any,
        router: Router
    ): void {
        const validTargets = ['timeline', 'risk_management', 'maps', 'forge'];

        if (!validTargets.includes(redirectionTarget)) {
            console.error(`❌ Target de redirection invalide: ${redirectionTarget}`);
            return;
        }

        const ctx: RedirectionContext = { redirectionTarget, chartQuery, clickedData, router };
        const matchedCase = this.redirectionCases.find(c => c.match(ctx));

        if (matchedCase) {
            matchedCase.action(ctx);
        } else {
            console.warn('⚠️ Aucun cas de redirection correspondant');
        }
    }

    // Fonctions privées

    private getFirstFieldKey(query: any): string | null {
        const fieldKeys = query.field ? Object.keys(query.field) : [];
        return fieldKeys.length > 0 ? (fieldKeys[0] ?? null) : null;
    }

    private getFirstAggregationKey(query: any): string | null {
        const aggKeys = query.aggregation ? Object.keys(query.aggregation) : [];
        return aggKeys.length > 0 ? (aggKeys[0] ?? null) : null;
    }

    private getNextBinning(currentBinning: string): string {
        const binningOrder = ['year', 'month', 'week', 'day', 'hour', 'minute', 'second'];
        const currentIdx = binningOrder.indexOf(currentBinning);

        return currentIdx !== -1 && currentIdx + 1 < binningOrder.length
            ? (binningOrder[currentIdx + 1] ?? currentBinning)
            : currentBinning;
    }

    private calculateDateRange(
        clickedTimestamp: number | undefined,
        binning: string
    ): { startDate: Date | null; endDate: Date | null } {
        if (!clickedTimestamp)
            return { startDate: null, endDate: null };

        const clickedDate = new Date(clickedTimestamp);
        let startTimestamp: number;
        let endTimestamp: number;

        switch (binning) {
            case 'year':
                startTimestamp = Date.UTC(clickedDate.getUTCFullYear(), 0, 1, 0, 0, 0, 0);
                endTimestamp = Date.UTC(clickedDate.getUTCFullYear() + 1, 0, 1, 0, 0, 0, 0);
                break;
            case 'month':
                startTimestamp = Date.UTC(clickedDate.getUTCFullYear(), clickedDate.getUTCMonth(), 1, 0, 0, 0, 0);
                endTimestamp = Date.UTC(clickedDate.getUTCFullYear(), clickedDate.getUTCMonth() + 1, 1, 0, 0, 0, 0);
                break;
            case 'week':
                const utcDate = clickedDate.getUTCDate();
                const utcDay = clickedDate.getUTCDay();
                const diff = (utcDay === 0 ? -6 : 1) - utcDay;
                startTimestamp = Date.UTC(
                    clickedDate.getUTCFullYear(),
                    clickedDate.getUTCMonth(),
                    utcDate + diff,
                    0, 0, 0, 0
                );
                endTimestamp = startTimestamp + (7 * 24 * 60 * 60 * 1000);
                break;
            case 'day':
                startTimestamp = Date.UTC(
                    clickedDate.getUTCFullYear(),
                    clickedDate.getUTCMonth(),
                    clickedDate.getUTCDate(),
                    0, 0, 0, 0
                );
                endTimestamp = startTimestamp + (24 * 60 * 60 * 1000);
                break;
            case 'hour':
                startTimestamp = Date.UTC(
                    clickedDate.getUTCFullYear(),
                    clickedDate.getUTCMonth(),
                    clickedDate.getUTCDate(),
                    clickedDate.getUTCHours(),
                    0, 0, 0
                );
                endTimestamp = startTimestamp + (60 * 60 * 1000);
                break;
            case 'minute':
                startTimestamp = Date.UTC(
                    clickedDate.getUTCFullYear(),
                    clickedDate.getUTCMonth(),
                    clickedDate.getUTCDate(),
                    clickedDate.getUTCHours(),
                    clickedDate.getUTCMinutes(),
                    0, 0
                );
                endTimestamp = startTimestamp + (60 * 1000);
                break;
            case 'second':
                startTimestamp = Date.UTC(
                    clickedDate.getUTCFullYear(),
                    clickedDate.getUTCMonth(),
                    clickedDate.getUTCDate(),
                    clickedDate.getUTCHours(),
                    clickedDate.getUTCMinutes(),
                    clickedDate.getUTCSeconds(),
                    0
                );
                endTimestamp = startTimestamp + 1000;
                break;
            default:
                return { startDate: null, endDate: null };
        }

        return {
            startDate: new Date(startTimestamp),
            endDate: new Date(endTimestamp)
        };
    }

    private createDateFilter(startDate: Date, endDate: Date): any {
        return {
            type: 'date',
            options: {
                dateType: 'Range',
                useHoursInterval: false,
                min: startDate.toISOString(),
                eqMin: true,
                max: endDate.toISOString(),
                eqMax: false,
                minHour: 0,
                minMinute: 0,
                maxHour: 23,
                maxMinute: 59
            }
        };
    }

    private createFieldCountQuery(baseQuery: any, fieldKey: string, fieldType: string): any {
        return {
            ...baseQuery,
            field: {
                [fieldKey]: {
                    type: fieldType,
                    options: {
                        sort: 'value',
                        order: 'ascending',
                        limit: 100
                    }
                }
            },
            aggregation: {
                [fieldKey]: {
                    type: fieldType,
                    options: {
                        dataset_name: '',
                        aggregate: 'count'
                    }
                }
            }
        };
    }

    private extractQueryParams(chartQuery: any, clickedData: any): any {
        const params: any = {};
        const cleanFieldName = (key: string) => key.split('.').pop() ?? key;

        // FIELD
        const fieldKey = chartQuery.field ? Object.keys(chartQuery.field)[0] : null;

        if (fieldKey) {
            const field = chartQuery.field[fieldKey];

            if (field?.type === 'date' && field.options?.binning) {
                const { startDate, endDate } = this.calculateDateRange(clickedData?.x, field.options.binning);
                if (startDate && endDate) {
                    params.startDate = startDate.toISOString();
                    params.endDate = endDate.toISOString();
                }
            } else if (clickedData?.x !== undefined) {
                params[cleanFieldName(fieldKey)] = clickedData.x;
            }
        }

        // AGGREGATION
        const aggKey = chartQuery.aggregation ? Object.keys(chartQuery.aggregation)[0] : null;

        if (aggKey) {
            const aggregation = chartQuery.aggregation[aggKey];
            const cleanName = cleanFieldName(aggKey);

            if (aggregation?.options?.aggregate === 'count') {
                if (clickedData?.y !== undefined) {
                    params[`${cleanName}_count`] = clickedData.y;
                }
                params[cleanName] = true;
            } else if (clickedData?.y !== undefined) {
                params[`${cleanName}_value`] = clickedData.y;
            }
        }

        // SERIES
        const seriesKey = chartQuery.series ? Object.keys(chartQuery.series)[0] : null;

        if (seriesKey && clickedData?.s !== undefined) {
            params[cleanFieldName(seriesKey)] = clickedData.s;
        }

        // FILTERS
        if (chartQuery.filters && Object.keys(chartQuery.filters).length > 0) {
            params.filters = JSON.stringify(chartQuery.filters);
        }

        return params;
    }
}